import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, ScrollView, StyleSheet, Alert, TouchableOpacity, ActivityIndicator, FlatList, Image } from 'react-native';
import { TextInput, Button, HelperText, Divider, Portal, Dialog, IconButton, Menu } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { useRoute, useNavigation } from '@react-navigation/native';
import productosApi from '../../services/productosApi';
import apiService from '../../services/api';
import { UNIDAD_ENTRY, buildEntryOptions, convertDisplayed, toBaseValues } from '../../utils/presentacionEntry';
import { markInventoryDirty } from '../../utils/inventoryRefresh';

const EditarProductoScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { producto } = route.params;
  const [loading, setLoading] = useState(false);
  const [loadingProducto, setLoadingProducto] = useState(true);
  const [loadingCategorias, setLoadingCategorias] = useState(true);
  const [categorias, setCategorias] = useState([]);
  const [showCategoriasMenu, setShowCategoriasMenu] = useState(false);
  const [showNuevaCategoriaDialog, setShowNuevaCategoriaDialog] = useState(false);
  const [nuevaCategoria, setNuevaCategoria] = useState('');

  const [formData, setFormData] = useState({
    nombre: '',
    codigo: '',
    descripcion: '',
    categoria: '',
    categoria_nombre: '',
    codigoBarras: '',
    marca: '',
    precioCompra: '',
    precioVenta: '',
    stock: '0',
    stockMinimo: '0',
    imagen: '',
    notas: ''
  });
  const [errors, setErrors] = useState({});

  // ===== Presentaciones =====
  const [presentacionesProducto, setPresentacionesProducto] = useState([]);
  const [catalogoPresentaciones, setCatalogoPresentaciones] = useState([]);
  const [showAddPresentacion, setShowAddPresentacion] = useState(false);
  const [showCatalogoMenu, setShowCatalogoMenu] = useState(false);
  const [editPresentacion, setEditPresentacion] = useState(null);
  const [selectedPresentacion, setSelectedPresentacion] = useState(null);
  const [factorInput, setFactorInput] = useState('');
  const [precioInput, setPrecioInput] = useState('');
  const [savingPresentacion, setSavingPresentacion] = useState(false);

  // ===== Captura de Precio/Stock por presentación =====
  // entryId = presentacion_id seleccionada para capturar (null = Unidad, factor 1)
  const [entryId, setEntryId] = useState(null);
  const [showEntryMenu, setShowEntryMenu] = useState(false);
  const entryOptions = useMemo(() => buildEntryOptions(presentacionesProducto), [presentacionesProducto]);
  const entry = entryOptions.find((o) => o.presentacion_id === entryId) || UNIDAD_ENTRY;

  const handleSelectEntry = (opcion) => {
    // Reinterpretar los valores ya escritos en la nueva presentación (misma base).
    const convertidos = convertDisplayed(
      { stock: formData.stock, precioVenta: formData.precioVenta, precioCompra: formData.precioCompra },
      entry.factor,
      opcion.factor
    );
    setFormData((prev) => ({ ...prev, ...convertidos }));
    setEntryId(opcion.presentacion_id);
    setShowEntryMenu(false);
  };

  // Si la presentación seleccionada para capturar deja de existir (se editó/eliminó),
  // volver a "Unidad" para no malinterpretar los valores escritos.
  useEffect(() => {
    if (entryId !== null && !entryOptions.some((o) => o.presentacion_id === entryId)) {
      setEntryId(null);
    }
  }, [entryOptions, entryId]);

  const loadPresentacionesProducto = useCallback(async () => {
    try {
      const data = await apiService.getPresentacionesProducto(producto.id);
      setPresentacionesProducto(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Error cargando presentaciones del producto:', e);
    }
  }, [producto.id]);

  const loadCatalogoPresentaciones = useCallback(async () => {
    try {
      const data = await apiService.getPresentaciones();
      setCatalogoPresentaciones(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Error cargando catálogo de presentaciones:', e);
    }
  }, []);

  const abrirAgregarPresentacion = () => {
    setEditPresentacion(null);
    setSelectedPresentacion(null);
    setFactorInput('');
    setPrecioInput('');
    setShowAddPresentacion(true);
  };

  const abrirEditarPresentacion = (pp) => {
    setEditPresentacion(pp);
    setSelectedPresentacion({ id: pp.presentacion_id, nombre: pp.nombre, abreviatura: pp.abreviatura });
    setFactorInput(String(pp.factor_conversion));
    setPrecioInput(pp.precio_venta != null && pp.precio_venta !== '' ? String(pp.precio_venta) : '');
    setShowAddPresentacion(true);
  };

  const guardarPresentacionProducto = async () => {
    const factor = parseFloat(factorInput);
    if (!editPresentacion && !selectedPresentacion) {
      Alert.alert('Falta dato', 'Selecciona la presentación');
      return;
    }
    if (isNaN(factor) || factor <= 0) {
      Alert.alert('Falta dato', 'El factor debe ser mayor a 0 (ej: 12, 24)');
      return;
    }
    setSavingPresentacion(true);
    try {
      const precioVenta = precioInput.trim() === '' ? null : (parseFloat(precioInput) || 0);
      if (editPresentacion) {
        await apiService.updatePresentacionProducto(producto.id, editPresentacion.id, { factor_conversion: factor, precio_venta: precioVenta });
      } else {
        await apiService.addPresentacionProducto(producto.id, {
          presentacion_id: selectedPresentacion.id,
          factor_conversion: factor,
          precio_venta: precioVenta,
        });
      }
      setShowAddPresentacion(false);
      await loadPresentacionesProducto();
    } catch (e) {
      Alert.alert('Error', e.message || 'No se pudo guardar la presentación');
    } finally {
      setSavingPresentacion(false);
    }
  };

  const eliminarPresentacionProducto = (pp) => {
    Alert.alert(
      'Quitar presentación',
      `¿Quitar "${pp.nombre}" del producto?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Quitar',
          style: 'destructive',
          onPress: async () => {
            try {
              await apiService.removePresentacionProducto(producto.id, pp.id);
              await loadPresentacionesProducto();
            } catch (e) {
              Alert.alert('Error', e.message || 'No se pudo quitar');
            }
          },
        },
      ]
    );
  };

  const presentacionesDisponibles = catalogoPresentaciones.filter(
    (c) => !presentacionesProducto.some((pp) => pp.presentacion_id === c.id)
  );

  const loadProductoData = useCallback(async () => {
    try {
      setLoadingProducto(true);
      const productoData = await productosApi.getProducto(producto.id);

      setFormData({
        nombre: productoData.nombre || '',
        codigo: productoData.codigo || '',
        descripcion: productoData.descripcion || '',
        categoria: productoData.categoria_id ? productoData.categoria_id.toString() : '',
        categoria_nombre: productoData.categoria_nombre || '',
        codigoBarras: productoData.codigo_barras || '',
        marca: productoData.marca || '',
        precioCompra: (productoData.precio_compra || 0).toString(),
        precioVenta: (productoData.precio_venta || 0).toString(),
        stock: ((productoData.stock_total ?? productoData.stock) || 0).toString(),
        stockMinimo: (productoData.stock_minimo || 0).toString(),
        imagen: productoData.imagen || '',
        notas: productoData.notas || ''
      });
    } catch (error) {
      console.error('Error al cargar datos del producto:', error);
      Alert.alert('Error', 'No se pudieron cargar los datos del producto');
    } finally {
      setLoadingProducto(false);
    }
  }, [producto.id]);

  useEffect(() => {
    loadProductoData();
    loadCategorias();
    loadPresentacionesProducto();
    loadCatalogoPresentaciones();
  }, [loadProductoData, loadPresentacionesProducto, loadCatalogoPresentaciones]);

  const loadCategorias = async () => {
    try {
      setLoadingCategorias(true);
      const response = await productosApi.getCategorias();
      setCategorias(response);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    } finally {
      setLoadingCategorias(false);
    }
  };

  const handleCrearCategoria = async () => {
    try {
      if (!nuevaCategoria.trim()) {
        Alert.alert('Error', 'El nombre de la categoría es obligatorio');
        return;
      }
      let nuevaCategoriaObj;
      try {
        nuevaCategoriaObj = await productosApi.createCategoria({ nombre: nuevaCategoria.trim() });
      } catch (error) {
        nuevaCategoriaObj = {
          id: categorias.length > 0 ? Math.max(...categorias.map(c => c.id)) + 1 : 1,
          nombre: nuevaCategoria.trim(),
          created_at: new Date().toISOString()
        };
      }
      setCategorias([...categorias, nuevaCategoriaObj]);
      handleChange('categoria', nuevaCategoriaObj.id.toString());
      setFormData(prev => ({ ...prev, categoria_nombre: nuevaCategoriaObj.nombre }));
      setNuevaCategoria('');
      setShowNuevaCategoriaDialog(false);
    } catch (error) {
      console.error('Error general al crear categoría:', error);
      Alert.alert('Error', 'No se pudo crear la categoría');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const pickImage = async () => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permiso denegado', 'Se necesita permiso para acceder a la galería');
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.8,
        base64: true,
      });
      if (!result.canceled) {
        handleChange('imagen', `data:image/jpeg;base64,${result.assets[0].base64}`);
      }
    } catch (error) {
      console.error('Error al seleccionar imagen:', error);
      Alert.alert('Error', 'No se pudo seleccionar la imagen');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es obligatorio';
    if (!formData.codigo.trim()) newErrors.codigo = 'El código es obligatorio';
    if (!formData.categoria) newErrors.categoria = 'La categoría es obligatoria';

    const pv = parseFloat(formData.precioVenta);
    if (!formData.precioVenta || isNaN(pv) || pv <= 0) newErrors.precioVenta = 'El precio de venta debe ser mayor a 0';

    if (formData.precioCompra) {
      const pc = parseFloat(formData.precioCompra);
      if (isNaN(pc) || pc < 0) newErrors.precioCompra = 'El costo debe ser un número válido';
    }

    const stock = parseInt(formData.stock);
    if (isNaN(stock) || stock < 0) newErrors.stock = 'El stock debe ser un número entero mayor o igual a 0';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true);
      // Convertir Precio/Stock capturados en la presentación elegida a unidades base.
      const base = toBaseValues(
        { stock: formData.stock, precioVenta: formData.precioVenta, precioCompra: formData.precioCompra },
        entry.factor
      );
      const productoData = {
        nombre: formData.nombre.trim(),
        codigo: formData.codigo.trim(),
        descripcion: formData.descripcion.trim() || null,
        categoria_id: formData.categoria ? parseInt(formData.categoria) : null,
        codigo_barras: formData.codigoBarras ? formData.codigoBarras.trim() : null,
        marca: formData.marca ? formData.marca.trim() : null,
        precio_compra: base.precio_compra,
        precio_venta: base.precio_venta,
        stock: base.stock,
        stock_minimo: parseInt(formData.stockMinimo) || 0,
        notas: formData.notas ? formData.notas.trim() : null,
        imagen: formData.imagen || null
      };

      await productosApi.updateProducto(producto.id, productoData);

      markInventoryDirty(); // que el listado se recargue al volver
      Alert.alert('Éxito', 'Producto actualizado correctamente', [
        { text: 'OK', onPress: () => navigation.goBack() }
      ]);
    } catch (error) {
      console.error('Error al actualizar producto:', error);
      Alert.alert('Error', 'No se pudo actualizar el producto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollView style={styles.container}>
      {(loading || loadingProducto) ? (
        <View style={styles.loaderContainer}>
          <ActivityIndicator size="large" color="#0066cc" />
          <Text style={styles.loaderText}>{loadingProducto ? 'Cargando datos del producto...' : 'Procesando...'}</Text>
        </View>
      ) : (
        <>
          <Text style={styles.title}>Editar Producto</Text>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Código *</Text>
            <TextInput
              style={styles.input}
              value={formData.codigo}
              onChangeText={(text) => handleChange('codigo', text)}
              placeholder="Código del producto"
              error={!!errors.codigo}
            />
            {errors.codigo && <HelperText type="error">{errors.codigo}</HelperText>}
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Nombre *</Text>
            <TextInput
              style={styles.input}
              value={formData.nombre}
              onChangeText={(text) => handleChange('nombre', text)}
              placeholder="Nombre del producto"
              error={!!errors.nombre}
            />
            {errors.nombre && <HelperText type="error">{errors.nombre}</HelperText>}
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Descripción</Text>
            <TextInput
              style={styles.input}
              value={formData.descripcion}
              onChangeText={(text) => handleChange('descripcion', text)}
              placeholder="Descripción del producto"
              multiline
              numberOfLines={3}
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Imagen del Producto</Text>
            <View style={styles.imageContainer}>
              {formData.imagen ? (
                <Image source={{ uri: formData.imagen }} style={styles.productImage} />
              ) : (
                <View style={styles.imagePlaceholder}>
                  <Ionicons name="image-outline" size={50} color="#cccccc" />
                  <Text style={styles.placeholderText}>Sin imagen</Text>
                </View>
              )}
              <Button mode="contained" onPress={pickImage} style={styles.imageButton}>
                {formData.imagen ? 'Cambiar imagen' : 'Seleccionar imagen'}
              </Button>
            </View>
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Categoría *</Text>
            {loadingCategorias ? (
              <View style={styles.loaderContainer}>
                <ActivityIndicator size="small" color="#0066cc" />
                <Text style={styles.loaderText}>Cargando categorías...</Text>
              </View>
            ) : (
              <View style={styles.categoryContainer}>
                <TouchableOpacity
                  style={[styles.categorySelector, !!errors.categoria && styles.inputError]}
                  onPress={() => setShowCategoriasMenu(true)}
                >
                  <Text style={styles.categorySelectorText}>
                    {formData.categoria
                      ? formData.categoria_nombre || categorias.find(c => c.id === parseInt(formData.categoria))?.nombre || 'Seleccionar categoría'
                      : 'Seleccionar categoría'}
                  </Text>
                  <Ionicons name="chevron-down" size={24} color="#666" />
                </TouchableOpacity>
                <IconButton icon="plus" size={24} onPress={() => setShowNuevaCategoriaDialog(true)} style={styles.addCategoryButton} />
              </View>
            )}
            {errors.categoria && <HelperText type="error">{errors.categoria}</HelperText>}

            <Menu
              visible={showCategoriasMenu}
              onDismiss={() => setShowCategoriasMenu(false)}
              anchor={{ x: 0, y: 0 }}
              style={styles.categoriesMenu}
            >
              <FlatList
                data={categorias}
                keyExtractor={(item) => (item.id || Math.random()).toString()}
                renderItem={({ item }) => (
                  <Menu.Item
                    title={item.nombre}
                    onPress={() => {
                      handleChange('categoria', (item.id || '').toString());
                      setFormData(prev => ({ ...prev, categoria_nombre: item.nombre }));
                      setShowCategoriasMenu(false);
                    }}
                  />
                )}
                ListEmptyComponent={
                  <View style={styles.emptyList}>
                    <Text>No hay categorías disponibles</Text>
                  </View>
                }
                style={styles.categoriesList}
              />
            </Menu>
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Código de Barras</Text>
            <TextInput
              style={styles.input}
              value={formData.codigoBarras}
              onChangeText={(text) => handleChange('codigoBarras', text)}
              placeholder="Código de barras (opcional)"
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Marca</Text>
            <TextInput
              style={styles.input}
              value={formData.marca}
              onChangeText={(text) => handleChange('marca', text)}
              placeholder="Ej: MK, Bayer, Genérico..."
            />
          </View>

          <Divider style={styles.divider} />

          <Text style={styles.sectionTitle}>Precios e Inventario</Text>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Ingresar precio y stock por</Text>
            <Menu
              visible={showEntryMenu}
              onDismiss={() => setShowEntryMenu(false)}
              anchor={
                <TouchableOpacity
                  style={styles.presentacionSelector}
                  onPress={() => setShowEntryMenu(true)}
                  activeOpacity={0.7}
                >
                  <Text style={styles.presentacionSelectorText} numberOfLines={1}>
                    {entry.nombre}{entry.factor !== 1 ? ` (1 = ${entry.factor} unidades)` : ''}
                  </Text>
                  <Ionicons name="chevron-down" size={22} color="#666" />
                </TouchableOpacity>
              }
            >
              {entryOptions.map((o) => (
                <Menu.Item
                  key={o.presentacion_id == null ? 'unidad' : o.presentacion_id}
                  title={o.factor !== 1 ? `${o.nombre} (1 = ${o.factor} unidades)` : o.nombre}
                  onPress={() => handleSelectEntry(o)}
                />
              ))}
            </Menu>
            <HelperText type="info" visible>
              Escribe los valores en {entry.nombre.toLowerCase()}; se guardan convertidos a unidades.
            </HelperText>
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>
              Precio de Venta (L.){entry.factor !== 1 ? ` por ${entry.nombre.toLowerCase()}` : ''} *
            </Text>
            <TextInput
              style={styles.input}
              value={formData.precioVenta}
              onChangeText={(text) => handleChange('precioVenta', text)}
              keyboardType="numeric"
              error={!!errors.precioVenta}
            />
            {entry.factor !== 1 && !!formData.precioVenta && (
              <HelperText type="info" visible>
                = L. {((parseFloat(formData.precioVenta) || 0) / entry.factor).toFixed(2)} por unidad
              </HelperText>
            )}
            {errors.precioVenta && <HelperText type="error">{errors.precioVenta}</HelperText>}
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>
              Costo (L.){entry.factor !== 1 ? ` por ${entry.nombre.toLowerCase()}` : ''}
            </Text>
            <TextInput
              style={styles.input}
              value={formData.precioCompra}
              onChangeText={(text) => handleChange('precioCompra', text)}
              keyboardType="numeric"
              error={!!errors.precioCompra}
            />
            {entry.factor !== 1 && !!formData.precioCompra && (
              <HelperText type="info" visible>
                = L. {((parseFloat(formData.precioCompra) || 0) / entry.factor).toFixed(2)} por unidad
              </HelperText>
            )}
            {errors.precioCompra && <HelperText type="error">{errors.precioCompra}</HelperText>}
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>
              Stock{entry.factor !== 1 ? ` (en ${entry.nombre.toLowerCase()})` : ''}
            </Text>
            <TextInput
              style={styles.input}
              value={formData.stock}
              onChangeText={(text) => handleChange('stock', text)}
              keyboardType="numeric"
              error={!!errors.stock}
            />
            {entry.factor !== 1 && !!formData.stock && (
              <HelperText type="info" visible>
                = {Math.round((parseFloat(formData.stock) || 0) * entry.factor)} unidades
              </HelperText>
            )}
            {errors.stock && <HelperText type="error">{errors.stock}</HelperText>}
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Stock Mínimo</Text>
            <TextInput
              style={styles.input}
              value={formData.stockMinimo}
              onChangeText={(text) => handleChange('stockMinimo', text)}
              keyboardType="numeric"
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Notas</Text>
            <TextInput
              style={styles.input}
              value={formData.notas}
              onChangeText={(text) => handleChange('notas', text)}
              placeholder="Notas adicionales (opcional)"
              multiline
              numberOfLines={3}
            />
          </View>

          <Divider style={styles.divider} />

          <View style={styles.presentacionesHeader}>
            <Text style={styles.sectionTitle}>Presentaciones</Text>
            <Button
              mode="contained-tonal"
              compact
              icon="plus"
              onPress={abrirAgregarPresentacion}
            >
              Agregar
            </Button>
          </View>
          <Text style={styles.presentacionesHint}>
            Ej: 1 Caja = 24 unidades, 1 Docena = 12. Al vender, el stock descuenta unidades reales.
          </Text>

          {presentacionesProducto.length === 0 ? (
            <Text style={styles.presentacionesEmpty}>
              Sin presentaciones. Las ventas se harán por unidad (factor 1).
            </Text>
          ) : (
            presentacionesProducto.map((pp) => (
              <View key={pp.id} style={styles.presentacionRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.presentacionNombre}>{pp.nombre}</Text>
                  <Text style={styles.presentacionFactor}>
                    1 {pp.nombre} = {Number(pp.factor_conversion)} unidades
                    {pp.precio_venta != null && pp.precio_venta !== ''
                      ? `  ·  L. ${Number(pp.precio_venta).toFixed(2)}`
                      : ''}
                  </Text>
                </View>
                <IconButton
                  icon="pencil"
                  size={20}
                  onPress={() => abrirEditarPresentacion(pp)}
                />
                <IconButton
                  icon="delete"
                  size={20}
                  iconColor="#DC3545"
                  onPress={() => eliminarPresentacionProducto(pp)}
                />
              </View>
            ))
          )}

          <View style={styles.buttonContainer}>
            <Button mode="contained" onPress={handleSubmit} style={styles.submitButton} loading={loading} disabled={loading}>
              Guardar Cambios
            </Button>
            <Button mode="outlined" onPress={() => navigation.goBack()} style={styles.cancelButton}>
              Cancelar
            </Button>
          </View>

          <Portal>
            <Dialog visible={showNuevaCategoriaDialog} onDismiss={() => setShowNuevaCategoriaDialog(false)}>
              <Dialog.Title>Nueva Categoría</Dialog.Title>
              <Dialog.Content>
                <TextInput
                  label="Nombre de la categoría"
                  value={nuevaCategoria}
                  onChangeText={setNuevaCategoria}
                  style={styles.dialogInput}
                />
              </Dialog.Content>
              <Dialog.Actions>
                <Button onPress={() => setShowNuevaCategoriaDialog(false)}>Cancelar</Button>
                <Button onPress={handleCrearCategoria}>Guardar</Button>
              </Dialog.Actions>
            </Dialog>

            <Dialog visible={showAddPresentacion} onDismiss={() => !savingPresentacion && setShowAddPresentacion(false)}>
              <Dialog.Title>{editPresentacion ? 'Editar presentación' : 'Agregar presentación'}</Dialog.Title>
              <Dialog.Content>
                {!editPresentacion && (
                  <>
                    <Text style={styles.label}>Presentación</Text>
                    <Menu
                      visible={showCatalogoMenu}
                      onDismiss={() => setShowCatalogoMenu(false)}
                      anchor={
                        <TouchableOpacity
                          style={styles.presentacionSelector}
                          onPress={() => setShowCatalogoMenu(true)}
                          activeOpacity={0.7}
                        >
                          <Text
                            style={[
                              styles.presentacionSelectorText,
                              !selectedPresentacion && styles.presentacionSelectorPlaceholder,
                            ]}
                            numberOfLines={1}
                          >
                            {selectedPresentacion ? selectedPresentacion.nombre : 'Seleccionar...'}
                          </Text>
                          <Ionicons name="chevron-down" size={22} color="#666" />
                        </TouchableOpacity>
                      }
                    >
                      {presentacionesDisponibles.length === 0 ? (
                        <Menu.Item title="No quedan presentaciones por asignar" disabled />
                      ) : (
                        presentacionesDisponibles.map((c) => (
                          <Menu.Item
                            key={c.id}
                            title={c.nombre}
                            onPress={() => {
                              setSelectedPresentacion(c);
                              setShowCatalogoMenu(false);
                            }}
                          />
                        ))
                      )}
                    </Menu>
                  </>
                )}
                <Text style={[styles.label, { marginTop: 12 }]}>
                  Equivalencia en unidades (ej: 12, 24)
                </Text>
                <TextInput
                  value={factorInput}
                  onChangeText={setFactorInput}
                  keyboardType="numeric"
                  placeholder="Cuántas unidades equivalen"
                  mode="outlined"
                />
                <Text style={[styles.label, { marginTop: 12 }]}>
                  Precio de venta de esta presentación (opcional)
                </Text>
                <TextInput
                  value={precioInput}
                  onChangeText={(t) => setPrecioInput(t.replace(/[^0-9.]/g, ''))}
                  keyboardType="decimal-pad"
                  placeholder="Vacío = precio por unidad × equivalencia"
                  mode="outlined"
                  left={<TextInput.Affix text="L." />}
                />
              </Dialog.Content>
              <Dialog.Actions>
                <Button onPress={() => setShowAddPresentacion(false)} disabled={savingPresentacion}>
                  Cancelar
                </Button>
                <Button
                  mode="contained"
                  onPress={guardarPresentacionProducto}
                  loading={savingPresentacion}
                  disabled={savingPresentacion}
                >
                  {editPresentacion ? 'Guardar' : 'Agregar'}
                </Button>
              </Dialog.Actions>
            </Dialog>
          </Portal>
        </>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, padding: 16, backgroundColor: '#fff' },
  categoryContainer: { flexDirection: 'row', alignItems: 'center' },
  categorySelector: {
    flex: 1, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    borderWidth: 1, borderColor: '#ddd', borderRadius: 4, padding: 10, backgroundColor: '#f9f9f9',
  },
  categorySelectorText: { fontSize: 16, color: '#333' },
  presentacionSelector: {
    width: '100%',
    minHeight: 52,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 6,
    paddingHorizontal: 14,
    paddingVertical: 12,
    backgroundColor: '#fff',
    marginBottom: 4,
  },
  presentacionSelectorText: { flex: 1, fontSize: 16, color: '#333', marginRight: 8 },
  presentacionSelectorPlaceholder: { color: '#999' },
  addCategoryButton: { marginLeft: 8 },
  categoriesMenu: { width: '80%', maxHeight: 300 },
  categoriesList: { maxHeight: 250 },
  emptyList: { padding: 16, alignItems: 'center' },
  dialogInput: { marginBottom: 10 },
  inputError: { borderColor: '#B00020' },
  loaderContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 },
  loaderText: { marginTop: 10, fontSize: 16, color: '#666' },
  title: { fontSize: 24, fontWeight: 'bold', marginBottom: 20, color: '#333' },
  formGroup: { marginBottom: 16 },
  label: { fontSize: 16, marginBottom: 8, color: '#333' },
  input: {
    backgroundColor: '#f9f9f9', borderWidth: 1, borderColor: '#ddd',
    borderRadius: 4, padding: 10, fontSize: 16,
  },
  divider: { marginVertical: 20, height: 1 },
  sectionTitle: { fontSize: 18, fontWeight: 'bold', color: '#333', marginBottom: 16 },
  buttonContainer: { marginTop: 20, marginBottom: 40 },
  submitButton: { marginBottom: 12, borderRadius: 4 },
  cancelButton: { borderRadius: 4 },
  imageContainer: { alignItems: 'center', marginVertical: 10 },
  productImage: { width: 200, height: 200, borderRadius: 8, marginBottom: 10 },
  imagePlaceholder: {
    width: 200, height: 200, borderRadius: 8, borderWidth: 1, borderColor: '#ddd',
    borderStyle: 'dashed', justifyContent: 'center', alignItems: 'center',
    marginBottom: 10, backgroundColor: '#f9f9f9',
  },
  placeholderText: { marginTop: 10, color: '#999' },
  imageButton: { marginTop: 10, width: 200 },
  presentacionesHeader: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4,
  },
  presentacionesHint: { fontSize: 12, color: '#6c757d', marginBottom: 12 },
  presentacionesEmpty: {
    fontSize: 13, color: '#6c757d', fontStyle: 'italic',
    paddingVertical: 12, paddingHorizontal: 12,
    backgroundColor: '#f8f9fa', borderRadius: 4, marginBottom: 8,
  },
  presentacionRow: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#f8f9fa', borderRadius: 6,
    paddingHorizontal: 12, paddingVertical: 4, marginBottom: 6,
  },
  presentacionNombre: { fontSize: 15, fontWeight: '600', color: '#212529' },
  presentacionFactor: { fontSize: 12, color: '#6c757d', marginTop: 2 },
});

export default EditarProductoScreen;
